// useAccountAdmin.js
import { useState, useEffect, useRef } from "react";
import { API, Auth, Hub } from "aws-amplify";
import { sortAlphabetical } from "util/dashboardManagement";

const ensureAuthed = async () => {
  // Gate all API calls on a valid Amplify session
  await Auth.currentAuthenticatedUser();
};

const useAccountAdmin = (match) => {
  const accountId = match?.params?.accountId;

  const [account, setAccount] = useState({});
  const [accountFetched, setAccountFetched] = useState(false);
  const [shouldUpdateAccount, updateAccount] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState({});
  const [allAccounts, setAllAccounts] = useState([]);
  const [allBrands, setAllBrands] = useState([]);

  // Track whether we have an authenticated session
  const [ready, setReady] = useState(false);

  // In-flight guards (prevents duplicate fetches in StrictMode)
  const acctInFlight = useRef(false);
  const profileInFlight = useRef(false);

  // --- Auth readiness: only set ready=true when a session exists ---
  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        await Auth.currentAuthenticatedUser();
        if (mounted) setReady(true);
      } catch {
        if (mounted) setReady(false);
      }
    })();

    const unsub = Hub.listen("auth", async ({ payload }) => {
      const evt = payload?.event;
      if (evt === "signIn" || evt === "cognitoHostedUI") {
        try {
          await Auth.currentAuthenticatedUser();
          if (mounted) setReady(true);
        } catch {
          if (mounted) setReady(false);
        }
      } else if (evt === "signOut") {
        if (mounted) {
          setReady(false);
          setAccount({});
          setAccountFetched(false);
          setCurrentUserProfile({});
        }
      }
    });

    return () => {
      mounted = false;
      unsub();
    };
  }, []);

  // Clear state when accountId changes (prevents showing stale data)
  useEffect(() => {
    setAccount({});
    setAccountFetched(false);
    setCurrentUserProfile({});
  }, [accountId]);

  // --- Fetch account details (only when authed & accountId present) ---
  useEffect(() => {
    if (!ready || !accountId) return;
    if (acctInFlight.current) return;
    acctInFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await ensureAuthed(); // ← NEW: hard gate before API
        const initialRequests = await Promise.allSettled([
          API.get("AccountGatewayApi", `/account/${accountId}`),
          API.get('AccountGatewayApi', '/accounts')
        ]);
        if (!cancelled) {
          let accountDetails;
          if (initialRequests[0].status === 'fulfilled') {
            accountDetails = initialRequests[0].value;
          }
          let accounts;
          if (initialRequests[1].status === 'fulfilled') {
            accounts = initialRequests[1].value;
          }

          const brandsSet = new Set();
          accounts.forEach(account => {
            const brandObject = { name: account.brand };
            brandsSet.add(brandObject);
          });
          const brands = [...brandsSet];
          brands.map(brand => {
            const brandAccounts = [];
            accounts.forEach(account => {
              if (account.brand === brand.name) {
                brandAccounts.push(account);
              }
            });
            brand['accounts'] = sortAlphabetical(brandAccounts, 'name');
          });
          const brandsWithAccounts = Array.from(new Set(brands.map(brandObject => JSON.stringify(brandObject)))).map(brandString => JSON.parse(brandString));

          setAllAccounts([...sortAlphabetical(accounts, 'name')]);
          setAllBrands([...sortAlphabetical(brandsWithAccounts, 'name')]);
          setAccount(accountDetails || {});
          setAccountFetched(true);
          updateAccount(false);
        }
      } catch (err) {
        if (!cancelled) setAccountFetched(true);
        console.error("error fetching accountDetails", err);
      } finally {
        acctInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, accountId, shouldUpdateAccount]);

  // --- Fetch current user's profile (only when authed) ---
  useEffect(() => {
    if (!ready) return;
    if (profileInFlight.current) return;
    profileInFlight.current = true;

    let cancelled = false;

    (async () => {
      try {
        await ensureAuthed(); // ← NEW: hard gate before API
        const profile = await API.get("AccountGatewayApi", "/profile");
        const acct =
          (profile?.accounts || []).find((a) => a.id === accountId) || {};
        if (!cancelled) setCurrentUserProfile(acct);
      } catch (err) {
        if (!cancelled)
          console.error("error fetching current user account profile", err);
      } finally {
        profileInFlight.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ready, accountId]);

  return {
    account,
    setAccount,
    shouldUpdateAccount,
    updateAccount,
    currentUserProfile,
    setCurrentUserProfile,
    accountFetched,
    allAccounts,
    allBrands
  };
};

export default useAccountAdmin;
